import { Button, Flex, Heading, Text } from "@radix-ui/themes";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { signInWithRedirect } from "aws-amplify/auth";

import { GoogleIcon, AppleIcon } from "@/assets/icons";
import OtcCodeInput from "@/components/ui/form/OtcCodeInput";
import { initiateOtcAuth, verifyOtcCode } from "@/lib/auth";
import { getOtcCooldownMs, saveOtcRequestTimestamp } from "@/lib/storage-utils";
import { useOtcLogin } from "@/components/providers/ApiProvider/hooks";
import { FiChevronLeft } from "react-icons/fi";

type VerifyCodeFormProps = {
  email: string;
  onSuccess: () => void;
  onPasswordFallback: () => void;
  onBack: () => void;
  compact?: boolean;
  authMethods?: string[];
};

export function VerifyCodeForm({
  email,
  onSuccess,
  onPasswordFallback,
  onBack,
  compact,
  authMethods,
}: VerifyCodeFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const otcLoginUser = useOtcLogin();

  // Google and Apple login are always enabled (flags removed)
  const googleEnabled = true;
  const appleEnabled = true;

  const socialProviders = useMemo(
    () =>
      authMethods?.filter(
        (m) =>
          (m === "google" && googleEnabled) || (m === "apple" && appleEnabled),
      ) ?? [],
    [authMethods, googleEnabled, appleEnabled],
  );

  const handleComplete = useCallback(
    async (code: string) => {
      setLoading(true);
      setError(false);
      setErrorMessage("");
      try {
        const { accessToken, idToken } = await verifyOtcCode(code);
        await otcLoginUser(email, accessToken, idToken);
        onSuccess();
      } catch {
        setError(true);
        setErrorMessage("Invalid code. Please try again.");
        setLoading(false);
      }
    },
    [email, otcLoginUser, onSuccess],
  );

  const handleResend = useCallback(async () => {
    const cooldownMs = getOtcCooldownMs(email);
    if (cooldownMs > 0) {
      const seconds = Math.ceil(cooldownMs / 1000);
      toast.info(`Please wait ${seconds}s before requesting a new code`);
      return;
    }

    try {
      await initiateOtcAuth(email);
      saveOtcRequestTimestamp(email);
      toast.success(`New code sent to ${email}`);
    } catch {
      toast.error("Failed to resend code. Please try again.");
    }
  }, [email]);

  return (
    <Flex
      className={`w-full ${compact ? "gap-4" : "gap-6 sm:gap-8"}`}
      direction="column"
      aria-labelledby="verify-code-form-title"
    >
      <div className="relative">
        <button
          type="button"
          onClick={onBack}
          className="absolute left-0 top-1 cursor-pointer text-blue-900 hover:text-blue-600"
          aria-label="Go back"
        >
          <FiChevronLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-2 text-center">
        <Heading
          id="verify-code-form-title"
          as="h2"
          weight="bold"
          className={`font-figtree text-blue-900 leading-[1.1] ${compact ? "text-[28px] tracking-[-0.28px]" : "text-[32px] tracking-[-0.32px] sm:text-[40px] sm:tracking-[-0.4px]"}`}
        >
          Enter Your Code
        </Heading>
        <Text size="2" className="text-gray-700">
          We've sent a code to <span className="font-semibold">{email}</span>
        </Text>
        <Text size="2" className="text-gray-700 block">
          Please enter it to continue.
        </Text>
      </div>

      <div className="space-y-3">
        <OtcCodeInput
          onComplete={handleComplete}
          error={error}
          disabled={loading}
        />
        {errorMessage && (
          <Text size="1" className="text-red-600 text-center block">
            {errorMessage}
          </Text>
        )}
      </div>

      <Flex direction="column" gap="3">
        <Flex direction="column" align="center" gap="2">
          <button
            type="button"
            onClick={handleResend}
            className="text-sm font-semibold text-blue-600 hover:text-blue-500 cursor-pointer"
            data-testid="auth-otc-resend"
          >
            Resend Code
          </button>

          {(!authMethods || authMethods.includes("password")) && (
            <button
              type="button"
              onClick={onPasswordFallback}
              className="text-sm text-gray-700 hover:text-blue-600 cursor-pointer"
            >
              Sign in with password
            </button>
          )}
        </Flex>

        {socialProviders.length > 0 && (
          <>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-a6" />
              <Text size="2" className="text-gray-8">
                or
              </Text>
              <div className="flex-1 h-px bg-gray-a6" />
            </div>

            <Flex direction="column" gap="2">
              {socialProviders.includes("google") && (
                <Button
                  onClick={() =>
                    signInWithRedirect({
                      provider: "Google",
                      customState: "google",
                      options: { prompt: "SELECT_ACCOUNT" },
                    })
                  }
                  type="button"
                  radius="full"
                  className="h-[40px] w-full !bg-white !border !border-solid !border-blue-900 hover:!bg-gray-100 text-blue-900 font-semibold text-[16px] cursor-pointer"
                >
                  <GoogleIcon className="inline-block mr-2" /> Sign in with
                  Google
                </Button>
              )}
              {socialProviders.includes("apple") && (
                <Button
                  onClick={() =>
                    signInWithRedirect({
                      provider: "Apple",
                      customState: "apple",
                    })
                  }
                  type="button"
                  radius="full"
                  className="h-[40px] w-full !bg-white !border !border-solid !border-blue-900 hover:!bg-gray-100 text-blue-900 font-semibold text-[16px] cursor-pointer"
                >
                  <AppleIcon className="inline-block mr-2 [&_path]:fill-black" />{" "}
                  Sign in with Apple
                </Button>
              )}
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  );
}

export default VerifyCodeForm;
